import { BreakPointCondition, UI, useBreakpointIf, Stack } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Button, Pressable } from 'native-base';
import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';

import {
  SignedInNavigationScreenNavigationProp,
  CustomerDetailStackParamList,
} from '../../../../../../../routes/types';
import { useCustomerDetailsActions } from '../../../../../../../store/customerDetail/actions';
import {
  customersAssetsDateSelected,
  isExportingPdfForCustomersAssets,
} from '../../../../../../../store/customerDetail/atom';
import { ICustomerInvestedAssetsNormalized } from '../../../../../../../store/customerDetail/types';
import { CustomerAssetDetail } from '../../../../../../CustomerAssetDetail';

export interface AssetCardProps {
  product: ICustomerInvestedAssetsNormalized['assetsNormalized'][0];
  openDetailsBelow?: boolean;
  hasDetails?: boolean;
}

const specificPdfDownloadExists = false;

export const AssetCard: React.FC<AssetCardProps> = ({
  product,
  openDetailsBelow = false,
  hasDetails = true,
}) => {
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const [isExpanded, setIsExpanded] = useState(false);
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();

  const route = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const { downloadCustomerAssetsDetailsPdf, setAssetsDetailProductSelected } =
    useCustomerDetailsActions();
  const isExportingPdf = useRecoilValue(isExportingPdfForCustomersAssets);
  const selectedDate = useRecoilValue(customersAssetsDateSelected);

  const buttonDisabled = isExportingPdf || !specificPdfDownloadExists;
  const baseChevronIcon = openDetailsBelow ? 'ChevronDown' : 'ChevronRight';
  const chevronIcon = isExpanded ? 'ChevronUp' : baseChevronIcon;

  const handleDownloadPdf = () => {
    downloadCustomerAssetsDetailsPdf({
      consolidatedDate: selectedDate || new Date(),
      accountId: route.params.accountId,
      marketType: product.marketNameNormalized,
    });
  };

  const handleDetailsClick = () => {
    setAssetsDetailProductSelected(product);

    if (openDetailsBelow) {
      setIsExpanded(!isExpanded);
      return;
    }

    navigate('CustomersAssetsDetails', {
      ...route.params,
      marketType: product.marketTypeNormalized,
    });
  };

  return (
    <>
      <UI.ProductCard
        loading={false}
        size={60}
        testId={product.marketNameNormalized}
        color={product.colorNormalized}
        product={{
          name: product.marketNameNormalized,
          percentage: product.percentageNormalized,
          value: product.valueNormalized,
        }}
        leftComponent={
          !isMobile && (
            <Stack pr={Spacing['inline.nano']}>
              <Button
                p={0}
                variant="textLink"
                size="lg"
                disabled={buttonDisabled || !specificPdfDownloadExists}
                _text={{
                  color: Color[buttonDisabled ? 'neutral.light' : 'brand.tertiary.principal'],
                }}
                onPress={handleDownloadPdf}
                rightIcon={
                  <UI.Icon
                    name="Pdf"
                    size="xs"
                    color={Color[buttonDisabled ? 'neutral.light' : 'brand.tertiary.principal']}
                  />
                }>
                EXPORTAR
              </Button>
            </Stack>
          )
        }
        rightComponent={
          hasDetails ? (
            <Stack pl={Spacing['inline.nano']}>
              <Pressable onPress={handleDetailsClick}>
                <UI.Icon name={chevronIcon} size="xs" color={Color['brand.primary.principal']} />
              </Pressable>
            </Stack>
          ) : undefined
        }
      />
      {isExpanded && <CustomerAssetDetail marketType={product.marketTypeNormalized} isInline />}
    </>
  );
};
